import { useState, useRef, useEffect } from 'react';
import { useAuthController } from '@/hooks/useAuthController';
import { Card } from '@/components/shared/card';
import { Alert, AlertDescription } from "@/components/shared/alert";
import { Button } from '@/components/shared/button';
import { Input } from '@/components/shared/input';
import { Label } from '@/components/shared/label';
import { AuthError } from '@supabase/supabase-js';
import MainLayout from '@/components/layout/MainLayout';
import { ROUTES } from '@/config/routes';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, Mail } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

const Login = () => {
  const { signIn, status, isLoading, user } = useAuthController();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const location = useLocation();
  
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const formRef = useRef<HTMLFormElement>(null);

  // Set email to current user's email if they're logged in
  useEffect(() => {
    if (user?.email && !email) {
      setEmail(user.email);
    }
  }, [user, email]);

  // Handle error state passed from ResetPassword component
  useEffect(() => {
    console.log('[Login] Location state:', location.state);
    if (location.state?.error && location.state?.fromPasswordReset) {
      console.log('[Login] Setting error from ResetPassword:', location.state.error);
      setError(location.state.error);
      // Clear the state to prevent showing the error again on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Focus management
  useEffect(() => {
    if (error && emailRef.current) {
      emailRef.current.focus();
    }
  }, [error]);

  const handleAuthError = (error: AuthError) => {
    console.error('Auth error:', error);
    let errorMessage = 'An error occurred during sign in.';
    
    if (error instanceof AuthError) {
      switch (error.message) {
        case 'Invalid login credentials':
          errorMessage = 'Invalid email or password. Please check your credentials and try again.';
          break;
        case 'Email not confirmed':
          errorMessage = 'Please check your email and confirm your account before signing in.';
          break;
        case 'Too many requests':
          errorMessage = 'Too many sign-in attempts. Please wait a moment before trying again.';
          break;
        default:
          errorMessage = error.message;
      }
    }
    
    setError(errorMessage);
    setMessage(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (!email || !password) {
      setError('Please enter both email and password.');
      return;
    }

    const { error } = await signIn(email, password);
    
    if (error) {
      handleAuthError(error);
    } else {
      setMessage('Signing in...');
      // The AuthController will handle the redirect
    }
  };

  const handlePasswordReset = async (e: React.MouseEvent) => {
    e.preventDefault();
    
    // Determine which email to use for password reset
    let emailToUse = email;
    
    // If user is logged in, use their email
    if (user?.email) {
      emailToUse = user.email;
    } else if (!email) {
      // Only prompt for email if user is not logged in and email field is empty
      setError('Please enter your email address first.');
      emailRef.current?.focus();
      return;
    }

    console.log('[Login] Attempting password reset for email:', emailToUse);
    setIsResettingPassword(true);
    setError(null);
    setMessage(null);

    try {
      // Try different redirect URL approaches
      const currentOrigin = window.location.origin;
      const redirectTo = `${currentOrigin}${ROUTES.RESET_PASSWORD}`;
      console.log('[Login] Redirect URL:', redirectTo);
      
      // First try with the redirect URL
      let { data, error } = await supabase.auth.resetPasswordForEmail(emailToUse, {
        redirectTo
      });
      
      // If that fails, try without redirect URL
      if (error) {
        console.log('[Login] First attempt failed, trying without redirect URL...');
        const { data: data2, error: error2 } = await supabase.auth.resetPasswordForEmail(emailToUse);
        data = data2;
        error = error2;
      }
      
      console.log('[Login] Password reset response:', { data, error });
      
      if (error) {
        console.error('[Login] Password reset error:', error);
        console.error('[Login] Error details:', {
          message: error.message,
          status: error.status,
          name: error.name
        });
        
        // Provide more specific error messages
        if (error.message.includes('rate limit')) {
          setError('Too many password reset attempts. Please wait a few minutes before trying again.');
        } else if (error.message.includes('not found')) {
          setError('No account found with this email address.');
        } else if (error.message.includes('disabled')) {
          setError('Password reset is currently disabled. Please contact support.');
        } else if (error.message.includes('redirect')) {
          setError('Invalid redirect URL. Please try again.');
        } else {
          setError(`Failed to send password reset email: ${error.message}. Please try again.`);
        }
        
        toast.error('Failed to send password reset email');
      } else {
        console.log('[Login] Password reset email sent successfully');
        setMessage('Password reset email sent! Please check your inbox.');
        setError(null);
        toast.success('Password reset email sent! Check your inbox.');
      }
    } catch (err: any) {
      console.error('[Login] Exception during password reset:', err);
      console.error('[Login] Exception details:', {
        message: err.message,
        stack: err.stack,
        name: err.name
      });
      setError('Failed to send password reset email. Please try again.');
      toast.error('Failed to send password reset email');
    } finally {
      setIsResettingPassword(false);
    }
  };

  const isFormDisabled = isLoading || status === 'loading' || isResettingPassword;

  return (
    <MainLayout>
      <div className="flex min-h-full items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md space-y-8">
          <div className="text-center">
            <h1 className="mt-6 text-3xl font-bold tracking-tight text-foreground">
              Welcome Back
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Sign in to your account to save and track your assessments
            </p>
          </div>
          
          <Card className="mt-8 p-6 bg-card">
            <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
              {error && (
                <Alert variant="destructive" className="mb-6" role="alert" aria-live="polite">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              {message && (
                <Alert className="mb-6" role="status" aria-live="polite">
                  <AlertDescription>{message}</AlertDescription>
                </Alert>
              )}
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="email" className="block text-sm font-medium text-foreground">
                    Email address
                  </Label>
                  <Input
                    ref={emailRef}
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isFormDisabled}
                    className="mt-1 block w-full"
                    aria-describedby={error ? "error-message" : undefined}
                    aria-invalid={!!error}
                  />
                </div>

                <div>
                  <Label htmlFor="password" className="block text-sm font-medium text-foreground">
                    Password
                  </Label>
                  <div className="relative mt-1">
                    <Input
                      ref={passwordRef}
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      required
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={isFormDisabled}
                      className="block w-full pr-10"
                      aria-describedby={error ? "error-message" : undefined}
                      aria-invalid={!!error}
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={isFormDisabled}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4 text-gray-400" />
                      ) : (
                        <Eye className="h-4 w-4 text-gray-400" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={handlePasswordReset}
                  disabled={isFormDisabled}
                  className={`text-sm transition-colors duration-200 ${
                    isResettingPassword 
                      ? 'text-gray-400 cursor-not-allowed' 
                      : 'text-primary hover:text-primary/80 disabled:text-gray-400 disabled:cursor-not-allowed'
                  }`}
                  aria-label={isResettingPassword ? "Sending password reset email..." : "Forgot your password?"}
                >
                  {isResettingPassword ? (
                    <>
                      <Mail className="inline h-4 w-4 mr-1 animate-pulse" />
                      Sending...
                    </>
                  ) : (
                    'Forgot your password?'
                  )}
                </button>
              </div>

              <Button
                type="submit"
                disabled={isFormDisabled}
                className="w-full"
                aria-busy={isFormDisabled}
              >
                {isFormDisabled ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {isResettingPassword ? 'Sending reset email...' : 'Signing in...'}
                  </>
                ) : (
                  'Sign in'
                )}
              </Button>

              <div className="text-center">
                <p className="text-sm text-muted-foreground">
                  Don't have an account?{' '}
                  <Link 
                    to={ROUTES.SIGNUP}
                    className="font-medium text-primary hover:text-primary/80 transition-colors"
                  >
                    Create one here
                  </Link>
                </p>
              </div>
            </form>
          </Card>
        </div>
      </div>
    </MainLayout>
  );
};

export default Login;